<i18n src="./resources/locales.json"></i18n>

<template>
  <div class="pointer" @contextmenu.prevent="onShowContextMenu($event)" @click="onSidebarInteractionClick()">
    <v-list-item
      class="interaction-item"
      :class="{
        blink: shouldBlink
      }"
    >
      <v-list-item-avatar>
        <EcsInteractionAvatar :interaction="interaction" :size="40" />
      </v-list-item-avatar>
      <div v-show="notifications > 0" class="notification-counter primary" :dark="$vuetify.theme.dark">
        {{ notifications }}
      </div>

      <v-list-item-content>
        <v-list-item-title class="interaction-title">
          {{ displayName }}
        </v-list-item-title>
        <v-list-item-subtitle class="interaction-sub-title">
          <span v-if="interaction.isHistoric">{{ timeAgo }}</span>
          <span v-else>{{ lastMessageStatus }}</span>
        </v-list-item-subtitle>
      </v-list-item-content>

      <v-list-item-action class="flip-container">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-icon
              v-show="!isTyping"
              class="will-flip"
              :class="{
                flip: hasFlip
              }"
              :style="{ color: interactionStatus.color }"
              v-on="on"
            >
              {{ interactionStatus.icon }}
            </v-icon>
          </template>
          <span>{{ $t(interactionStatus.statusName, {}) }}</span>
        </v-tooltip>
        <div v-show="isTyping">
          <EcsTypingAnimation class="typing-animation" />
        </div>
      </v-list-item-action>
    </v-list-item>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import EcsSideBarInteractionItemMixin from '@/mixins/ecs-side-bar-interaction-mixins/EcsSideBarInteractionItemMixin';
import EcsInteractionAvatar from '@/components/ecs-base-interaction-components/ecs-avatar/EcsInteractionAvatar';
import EcsTypingAnimation from '@/components/ecs-typing-animation/EcsTypingAnimation';
import { SYSTEM } from '@/constants/socket-events';

export default {
  name: 'EcsSideBarChatInteractionItem',

  components: { EcsInteractionAvatar, EcsTypingAnimation },

  mixins: [EcsSideBarInteractionItemMixin],

  props: {
    interaction: {
      type: Object,
      required: true
    }
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),

    ...mapGetters('CONVERSATIONS', ['getConversationsForInteraction']),

    ...mapGetters('ELEMENTS', ['getElementsForInteraction']),

    displayName() {
      return this.interaction.formattedCustomerFullName || this.interaction.receptor.name;
    },

    conversations() {
      return this.getConversationsForInteraction(this.interaction);
    },

    lastMessage() {
      let result;

      if (this.conversations.length > 0) {
        const conversationReducer = (mostRecentConversation, conversation) => {
          return mostRecentConversation.timestamp > conversation.timestamp ? mostRecentConversation : conversation;
        };
        result = this.conversations.reduce(conversationReducer, this.conversations[0]).lastMessage;
      }

      return result;
    },

    isTyping() {
      return this.conversations.some(conversation => conversation.isTyping.length > 0) && !this.interaction.isHandled;
    },

    isLastMessageFromLoggedInAgent() {
      let result;

      if (this.lastMessage) {
        result = this.lastMessage.type === SYSTEM || this.lastMessage.originator.id === this.loggedInAgent.id;
      }

      return result;
    },

    lastMessageStatus() {
      let result;

      if (this.lastMessage) {
        const date = this.fromNow(this.lastMessage.timestamp, 'UTC').toLowerCase();
        const status = this.isLastMessageFromLoggedInAgent ? this.$t('sent') : this.$t('received');
        result = `${status} ${date}`;
      }

      return result;
    },

    interactionStatus() {
      let result;

      const [mainElement] = this.getElementsForInteraction(this.interaction);

      if (this.interaction.isHandled) {
        if (this.interaction.timing.answeredWithinGoalTime) {
          result = this.iconMap.answeredWithinGoalTime;
        } else {
          result = this.iconMap.completed;
        }
      } else if (mainElement && mainElement.isOnHold) {
        result = this.iconMap.onHold;
      } else {
        result =
          this.iconMap[this.interaction.interactionStateId] ||
          (this.isLastMessageFromLoggedInAgent ? this.iconMap.sentMessage : this.iconMap.receivedMessage);
      }

      return result;
    },

    hasFlip() {
      return !this.interaction.isHistoric && !this.isLastMessageFromLoggedInAgent;
    }
  }
};
</script>

<style src="./scoped.css" scoped />
